import { createVuetify } from 'vuetify';
import { THEME_LIGHT, vuetifyThemes } from './vuetifyThemes.js';

/**
 * @typedef {Object} DashboardVuetifyOptions
 * @property {string} [defaultTheme=THEME_LIGHT]  theme name Vuetify boots with
 * @property {Record<string, object>} [themeOverrides] per-theme partial overrides,
 *   merged over `vuetifyThemes` one theme at a time (`{ dark: { colors: {...} } }`)
 * @property {object} [icons]       Vuetify icon config — app-owned (icon fonts are an app dependency)
 * @property {object} [components]  component map for explicit registration
 * @property {object} [directives]  directive map for explicit registration
 */

/**
 * The PURE half of `createDashboardVuetify`: builds the options object without
 * touching Vuetify, so it can be diffed/unit-tested without a DOM.
 *
 * `icons`, `components` and `directives` are only emitted when the caller
 * passes them, so the result is byte-for-byte what the app used to write by
 * hand rather than an object studded with `undefined` keys.
 *
 * @param {DashboardVuetifyOptions} [options]
 * @returns {{ theme: { defaultTheme: string, themes: Record<string, object> },
 *             icons?: object, components?: object, directives?: object }}
 */
export function buildVuetifyOptions({
  defaultTheme = THEME_LIGHT,
  themeOverrides = {},
  icons,
  components,
  directives,
} = {}) {
  const themes = {};
  for (const [name, definition] of Object.entries(vuetifyThemes)) {
    themes[name] = themeOverrides[name] ? { ...definition, ...themeOverrides[name] } : definition;
  }
  // An override for a theme name we don't ship (a dashboard adding its own) is
  // taken as a whole theme definition.
  for (const [name, definition] of Object.entries(themeOverrides)) {
    if (!themes[name]) themes[name] = definition;
  }

  const options = { theme: { defaultTheme, themes } };
  if (components) options.components = components;
  if (directives) options.directives = directives;
  if (icons) options.icons = icons;
  return options;
}

/**
 * Creates the one Vuetify instance a dashboard app installs. Same behaviour as
 * the `createVuetify({ theme: { defaultTheme: 'light', themes: { dark, light } }, … })`
 * call every app used to inline — the two theme definitions now come from
 * `@rdrive/theme`'s tokens instead of being copy-pasted per dashboard.
 *
 * @param {DashboardVuetifyOptions} [options]
 * @returns {ReturnType<typeof createVuetify>}
 *
 * @example
 * app.use(createDashboardVuetify({
 *   defaultTheme: THEME_LIGHT,
 *   icons: { defaultSet: 'mdi', sets: { mdi, md, fa } },
 *   components,
 *   directives,
 * }));
 */
export function createDashboardVuetify(options = {}) {
  return createVuetify(buildVuetifyOptions(options));
}
